import React, { type FC, type PropsWithChildren, useState, useEffect, useRef } from 'react';

import type { Lucid } from '../../types/types';
import Box from '../box/box';
import FeaturedSnippet from './featured-snippet';
import BlogImage from './blog-image';
import HtmlBlock from './html-block';
import TechblogHtmlBlock from './techblog-html-block';
import InTextCta from './in-text-cta';
import BlogVideo from './blog-video';
import BlogTOC from '../blog-toc/blog-toc';

type BlogCollections =
  | 'blog_featured_snippet'
  | 'blog_clickable_image'
  | 'blog_html'
  | 'blog_inline_cta'
  | 'tech_blog_html'
  | 'video';
export type BlogComponentCollection = {
  collection: BlogCollections;
  id: string;
  [key: string]: any;
};

export interface BlogContentProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The Drupal blog content paragraph entities */
  content: Lucid.Directus.Generated.Blog_Post_Translations_Content_Blocks[];
  /** Determines whether or not the TOC should be shown */
  shouldShowTOC?: boolean;
}

/**
 * renderBlogContent()
 * Displays the blog content sections
 */
const renderBlogContent = (
  content: Lucid.Directus.Generated.Blog_Post_Translations_Content_Blocks[],
  sectionRefs: React.MutableRefObject<HTMLHeadingElement[]>,
) => (
  <>
    {content?.map(block => {
      const { collection, item, id } = block;

      switch (collection) {
        case 'blog_featured_snippet':
          return <FeaturedSnippet key={id} item={item as Lucid.Directus.Generated.Blog_Featured_Snippet} />;
        case 'blog_clickable_image':
          return <BlogImage key={id} item={item as Lucid.Directus.Generated.Blog_Clickable_Image} />;
        case 'video':
          return <BlogVideo key={id} item={item as Lucid.Directus.Generated.Video} />;
        case 'blog_html':
          return <HtmlBlock key={id} item={item as Lucid.Directus.Generated.Blog_Html} sectionRefs={sectionRefs} />;
        case 'blog_inline_cta':
          return <InTextCta key={id} item={item as Lucid.Directus.Generated.Blog_Inline_Cta} />;
        case 'tech_blog_html':
          return <TechblogHtmlBlock key={id} item={item as Lucid.Directus.Generated.Tech_Blog_Html} />;
        default:
          return null;
      }
    })}
  </>
);

/**
 * BlogContent
 */
const BlogContent: FC<PropsWithChildren<BlogContentProps>> = ({ content, className, shouldShowTOC, ...rest }) => {
  const [currentSection, setCurrentSection] = useState<string | null>(null);
  const sectionRefs = useRef<HTMLHeadingElement[]>([]);

  useEffect(() => {
    const currentHeadings = sectionRefs.current;
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            setCurrentSection(id);
          }
        });
      },
      {
        rootMargin: '0px 0px -50% 0px',
        threshold: 0.0,
      },
    );

    currentHeadings.forEach(heading => {
      observer.observe(heading);
    });

    return () => {
      currentHeadings.forEach(heading => {
        observer.unobserve(heading);
      });
    };
  }, []);

  return (
    <>
      {shouldShowTOC ? (
        <>
          <Box
            {...rest}
            css={theme => ({
              label: 'blogContent',
              [theme.breakpoints.bp900.min]: { display: 'grid' },
              gridTemplateColumns: '264px 1fr',
            })}
            // eslint-disable-next-line @stylistic/jsx-sort-props
            className={className}
          >
            <BlogTOC content={content} currentSection={currentSection || undefined} />
            <Box
              css={theme => ({
                [theme.breakpoints.bp900.min]: {
                  paddingLeft: theme.space.s24,
                  borderLeft: `solid ${theme.colors.coolGrey[20]} 1px`,
                },
              })}
            >
              {renderBlogContent(content, sectionRefs)}
            </Box>
          </Box>
          <Box
            css={theme => ({
              display: 'none',
              [theme.breakpoints.bp900.min]: {
                display: 'block',
              },
              height: theme.space.s64,
              borderTop: `solid ${theme.colors.coolGrey[20]} 1px`,
              marginTop: theme.space.s64,
            })}
          />
        </>
      ) : (
        <>{renderBlogContent(content, sectionRefs)}</>
      )}
    </>
  );
};

export { BlogContent };
export default BlogContent;
